import { useCallback, useEffect, useState } from 'react';
import type { SurveySettings } from '../types/survey';
import { clampSettings, DEFAULT_SETTINGS } from '../utils/survey';

const STORAGE_KEY = 'aigleSurveySettings';

function readSettings(questionLimit: number): SurveySettings {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) return clampSettings(DEFAULT_SETTINGS, questionLimit);

    const parsed = JSON.parse(saved) as Partial<SurveySettings>;
    return clampSettings({ ...DEFAULT_SETTINGS, ...parsed }, questionLimit);
  } catch {
    return clampSettings(DEFAULT_SETTINGS, questionLimit);
  }
}

export function usePersistentSettings(questionLimit: number) {
  const [settings, setSettings] = useState<SurveySettings>(() =>
    readSettings(questionLimit),
  );

  const updateSettings = useCallback(
    (nextSettings: SurveySettings) => {
      const safeSettings = clampSettings(nextSettings, questionLimit);
      setSettings(safeSettings);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safeSettings));
    },
    [questionLimit],
  );

  useEffect(() => {
    const root = document.documentElement;
    document.body.classList.toggle('mode-fr', settings.language === 'fr');
    document.body.classList.toggle('mode-ar', settings.language === 'ar');
    document.body.classList.toggle('mode-bilingual', settings.language === 'bilingual');
    root.lang = settings.language === 'ar' ? 'ar' : 'fr';
    root.dir = 'ltr';
  }, [settings.language]);

  return { settings, updateSettings };
}
